import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Zap, Gem, CreditCard, ArrowUpRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/context/LanguageContext';
import { Link } from 'react-router-dom';

export function ServicesSection() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const services = [
    { icon: Zap, title: t('services.events'), desc: t('services.eventsDesc'), link: '/events', color: 'primary', badge: 'Popular' },
    { icon: Gem, title: t('services.studio'), desc: t('services.studioDesc'), link: '/studio', color: 'accent', badge: 'Premium' },
    { icon: CreditCard, title: t('services.nfc'), desc: t('services.nfcDesc'), link: '/c/alex-chen', color: 'primary', badge: 'New' },
  ];

  return (
    <section ref={ref} className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-title mb-4">
            {t('services.title')}
          </h2>
          <p className="text-base text-body max-w-lg">
            {t('services.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((service, i) => {
            const Icon = service.icon;
            const isPrimary = service.color === 'primary';
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.15 }}
              >
                <Link to={service.link}>
                  <Card className="group flex flex-col h-full bg-card border-border hover:border-primary/20 cursor-pointer" style={{ transition: 'border-color 0.3s ease' }}>
                    <CardHeader>
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${isPrimary ? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent'}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <Badge variant="outline" className={`text-[10px] tracking-wider uppercase ${isPrimary ? 'border-primary/30 text-primary' : 'border-accent/30 text-accent'}`}>
                          {service.badge}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg font-semibold text-title group-hover:text-primary" style={{ transition: 'color 0.3s ease' }}>
                        {service.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col">
                      <CardDescription className="text-sm text-body leading-relaxed flex-1">
                        {service.desc}
                      </CardDescription>
                      <div className="flex items-center gap-1 mt-6 text-sm text-primary font-medium opacity-0 group-hover:opacity-100" style={{ transition: 'opacity 0.3s ease' }}>
                        Explore
                        <ArrowUpRight className="w-4 h-4" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
